/**
 * Cloud implementation of the shared AccessControlApi.
 *
 * Cloud is multi-node: cameras/groups live on each node and a cloud user's access
 * is scoped per (user, node). Like the other cloud adapters, this binds the
 * globally-selected node id (localStorage, kept in sync by NodeContext) at request
 * time, so the shared page stays node-unaware and just refetches on nodeKey change.
 *
 * Only cloud users that hold a grant on the selected node are listed. The scope is
 * persisted on the cloud (user_node_access.group_ids/camera_ids) and pushed to the
 * node via the provisioning pipeline, which writes it into the node's own
 * user_group_access/user_camera_access tables.
 */
import type { AccessControlApi, User, Group, CameraRef, AccessSelection } from '@trinetra/shared';
import { getNodeGroups, getNodeCameras, getGroupCameras } from '../services/api.service';
import { userService } from '../services/user.service';
import { selectedNodeId } from './node';

function toArray(res: any): any[] {
  return Array.isArray(res) ? res : res?.data || [];
}

function toIdArray(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[];
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
}

export const cloudAccessControlApi: AccessControlApi = {
  async listUsers(): Promise<User[]> {
    const nodeId = selectedNodeId();
    if (!nodeId) return [];
    const rows = await userService.getNodeUsers(nodeId);
    return (rows || []).map((r: any) => ({
      id: r.user_id,
      name: r.name || r.email,
      email: r.email,
      role: r.node_role_name || r.cloud_role,
      is_active: r.is_active,
    }));
  },

  async listGroups(): Promise<Group[]> {
    const nodeId = selectedNodeId();
    if (!nodeId) return [];
    return toArray(await getNodeGroups(nodeId)) as Group[];
  },

  async listCameras(): Promise<CameraRef[]> {
    const nodeId = selectedNodeId();
    if (!nodeId) return [];
    return toArray(await getNodeCameras(nodeId)) as CameraRef[];
  },

  async groupCameras(groupId): Promise<CameraRef[]> {
    const nodeId = selectedNodeId();
    if (!nodeId) return [];
    return toArray(await getGroupCameras(nodeId, groupId, true)) as CameraRef[];
  },

  async getAccess(userId): Promise<AccessSelection> {
    const nodeId = selectedNodeId();
    if (!nodeId) return { groupIds: [], cameraIds: [] };
    const { grants } = await userService.getNodeAccess(userId);
    const grant = (grants || []).find((g: any) => g.node_id === nodeId);
    return {
      groupIds: toIdArray(grant?.group_ids),
      cameraIds: toIdArray(grant?.camera_ids),
    };
  },

  async setAccess(userId, selection): Promise<void> {
    const nodeId = selectedNodeId();
    if (!nodeId) throw new Error('No node selected');
    await userService.setNodeScope(userId, nodeId, selection.groupIds, selection.cameraIds);
  },
};
